import * as dayjs from 'dayjs';
import * as dayjsDe from 'dayjs/locale/de';
import {Inject, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {environment} from 'src/environments/environment';
import {CookieService} from '../services/cookie.service';
import {NGXLogger} from 'ngx-logger';
import {EMPTY, Observable, of, ReplaySubject, Subject} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {Language} from '../constants/language';
import {BsLocaleService} from 'ngx-bootstrap/datepicker';
import {ScgHttpWrapper} from '../injectables/scg-http-wrapper';
import {DOCUMENT, Location} from '@angular/common';
import {DeviceType} from '@app/core/models/device';
import {Router} from '@angular/router';
import {catchError, first, switchMap, tap} from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  /**
   * Default URL to which should redirected to on successful login
   */
  public static get defaultLoginRedirectUrl(): string {
    return AuthService._defaultLoginRedirectUrl;
  }
  private static _defaultLoginRedirectUrl = '/';

  /**
   * Default URL to which should redirected to on successful logout
   */
  public static readonly defaultLogoutRedirectUrl = '/login';

  private onLoginSubject = new ReplaySubject<boolean>(1);
  private onLogoutSubject = new Subject<void>();

  /** Used to make sure that only one logout is performed simultaneously */
  private logoutSubject = new Subject<LogoutResult>();
  private logoutInProgress = false;

  private _isLoggedIn = false;
  private _role       = UserRole.None;
  private _sessionId  = '';
  private _initialActiveDevice?: { pcId: string, user: string, deviceType: DeviceType };

  /**
   * Saves the called URL if the request was intercepted by the login screen
   * so that the user can be redirected after login.
   *
   * If no redirect location was given, it will contain the defaultLoginRedirectUrl.
   */
  public get loginRedirectUrl(): string {
    return this._loginRedirectUrl ?? AuthService.defaultLoginRedirectUrl;
  }
  public set loginRedirectUrl(value: string) {
    this._loginRedirectUrl = value;
  }
  private _loginRedirectUrl?: string;

  /**
   * Emits if user logged in
   *
   * Emits boolean (let's call it 'apiLogin') which is true if the login was performed with a real API request.
   * The boolean is false if the login is performed only using an already existing cookie containing the sessionId.
   *
   * If the user is already logged in when subscribing to this Observable, it will emit instantly.
   */
  public get onLogin(): Observable<boolean> {
    return this.onLoginSubject.asObservable();
  }

  /**
   * Emits if user logged out
   */
  public get onLogout(): Observable<void> {
    return this.onLogoutSubject.asObservable();
  }

  public get isLoggedIn(): boolean {
    return this._isLoggedIn;
  }

  public get role(): UserRole {
    return this._role;
  }

  /**
   * Returns sessionId if logged in or empty string otherwise
   */
  public get sessionId(): string {
    return this._sessionId;
  }

  /** Whether the app was launched in context of CB3 (this e.g. usually tells us that the header needs to be hidden in some views) */
  public get isCb3(): boolean {
    if (this._isCb3 == null) {
      this._isCb3 = this.cookie.getCookie('isCb3') != null;
    }
    return this._isCb3;
  }
  public set isCb3(value: boolean) {
    this._isCb3 = value;

    // No cookie = false
    if (value) {
      this.cookie.setCookie({ name: 'isCb3', value: '1', path: '/', sameSite: 'Lax', secure: true });
    } else {
      this.cookie.deleteCookie('isCb3');
    }

  }
  /** Initially undefined because the getter fetches the initial value from the cookie */
  private _isCb3: boolean;
  private _isParentsApp: boolean;


  /** Whether the app was launched in context of Win32 TWebBrowser of Child Control (needed for logout redirect to static logout page) */
  public get isCCLogin(): boolean {
    if (this._isCCLogin == null) {
      this._isCCLogin = this.cookie.getCookie('isCCLogin') != null;
    }
    return this._isCCLogin;
  }
  public set isCCLogin(value: boolean) {
    this._isCCLogin = value;

    // No cookie = false
    if (value) {
      this.cookie.setCookie({ name: 'isCCLogin', value: '1', path: '/', sameSite: 'Lax', secure: true });
    } else {
      this.cookie.deleteCookie('isCCLogin');
    }

  }

  /** Global bool var to check if user entered via browser as parentsApp (do not nix up with parents tokenlogin) */
  public get isParentsApp(): boolean {
    if (this._isParentsApp == null) {
      this._isParentsApp = localStorage?.getItem('isParentsApp')  != null;
    }
    return this._isParentsApp;
  }
  public set isParentsApp(value: boolean) {
    this._isParentsApp = value;

    if (value) {
      localStorage?.setItem('isParentsApp', 'true');
    } else {
      localStorage?.removeItem('isParentsApp');
    }

  }


  /** Initially undefined because the getter fetches the initial value from the cookie */
  private _isCCLogin: boolean;

  /**
   * Returns info about the device/user which is initially active after login.
   *
   * initialActiveDevice will only contain data if the login was performed using an API request
   * (this means after login using an already existing cookie, this will be undefined).
   *
   * This info was sent from the API on login and is usally only needed by the deviceService to perform the initial device switch.
   */
  public get initialActiveDevice(): { pcId: string, user: string, deviceType: DeviceType } | undefined {
    return this._initialActiveDevice;
  }

  constructor(
    private cookie: CookieService,
    private http: HttpClient,
    private logger: NGXLogger,
    private translate: TranslateService,
    private bsLocaleService: BsLocaleService,
    private scgHttp: ScgHttpWrapper,
    private router: Router,
    private location: Location,
    @Inject(DOCUMENT) private document: Document
  ) { }

  public init() {
    // Check if session cookie exists to set the current status of the user as logged in
    const sid = this.cookie.getCookie('sessionId');
    const deviceId = this.cookie.getCookie('pcId');
    const username = this.cookie.getCookie('user');
    const deviceType = this.cookie.getCookie('deviceType');
    const role = this.cookie.getCookie('role');

    /*
     * Don't login on tokenlogin because if we already have a session id, it will be replaced by tokenlogin
     * Also, the saved session id may be outdated and we don't want to login with an outdated session id
     * if we login with the correct session afterwards. This also prevents unintended behavior like redirect to login.
     */
    if (sid && !this.location.path().includes('tokenlogin')) {
      this._isLoggedIn = true;
      this._sessionId = sid;

      if (role === UserRole.Report) {
        this._role = UserRole.Report;
        AuthService._defaultLoginRedirectUrl = '/readonly-report';
      } else if (role === UserRole.Parents) {
        this._role = UserRole.Parents;
        AuthService._defaultLoginRedirectUrl = '/parents';
      } else {
        this._role = UserRole.Default;
        AuthService._defaultLoginRedirectUrl = '/';
      }

      this._initialActiveDevice = {
        pcId: deviceId,
        user: username,
        deviceType: Number.parseInt(deviceType, 10) < 10 ? DeviceType.CC : DeviceType.CB
      };

      this.logger.info('Found sessionId, assuming user is logged in');
      this.onLoginSubject.next(false);
    } else {
      // Cleanup just to be safe that tokenlogin works correctly and we have no old cookies in general
      this.cleanupSessionCookies();
    }
  }

  public login(email: string, password: string): Observable<LoginResult> {
    const body = {
      browsertime: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      language: this.translate.currentLang,
      username: email,
      password
    };

    return new Observable<LoginResult>(subscriber => {
      this.scgHttp.withoutSync().post<LoginResponse>(environment.apiUrl + 'login', body).subscribe({
        next: (response) => {
          subscriber.next(this.processLoginResponse(response));
        },
        error: (error) => {
          subscriber.next(this.processLoginError(error));
        },
        complete() { subscriber.complete(); }
      });
    });
  }

  /**
   * @param roleOverride Override role which is assigned by the API by default
   */
  public tokenlogin(token: string, roleOverride?: UserRole): Observable<LoginResult> {
    let loginObservable: Observable<LoginResponse|never> = EMPTY;

    loginObservable = this.http.get<LoginResponse>(environment.apiUrl + 'login/renewsession/' + token);

    return new Observable<LoginResult>(subscriber => {
      loginObservable.subscribe({
        next: (response) => {
          subscriber.next(this.processLoginResponse(response, roleOverride));
        },
        error: (error) => {
          subscriber.next(this.processLoginError(error));
        },
        complete() { subscriber.complete(); }
      });
    });
  }

  /**
   * logout as method (takes care of report and parents Role)
   *
   * Calls internally logout observable and redirects due to role to login or logout page
   */
  public logout(redirectOnLogout = true): void {
    this.logoutObserved(redirectOnLogout).subscribe();
  }

  /**
   * Performs logout
   *
   * If user is not logged in, LogoutResult.Success is returned and local data is cleaned to make sure everything is clean
   */
  public logoutObserved(redirectOnLogout = true): Observable<LogoutResult> {
    if (!this.logoutInProgress) {
      // Perform logout once no matter how often one subscribes during logout process

      let apiLogoutObservable: Observable<LogoutResult> = of(LogoutResult.Success);

      // Perform API logout if required
      if (this.isLoggedIn) {
        const body = {
          session: this.cookie.getCookie('sessionId')
        };

        apiLogoutObservable = this.scgHttp.withoutSync().post(environment.apiUrl + 'login/logout', body)
          .pipe(
            catchError(error => {
              if (error.error instanceof ErrorEvent) {
                // Client side/network error
                this.logger.error('API logout client side or network error', error);
                return of(LogoutResult.ClientError);
              } else {
                // If we get an HTTP 401 response, we are not logged out here
                // But automatic logout will happen in the interceptor in this case
                this.logger.error('API logout HTTP error', error);
                return of(LogoutResult.HttpError);
              }
            }),
            switchMap(() => {
              this.logger.info('API logout successful');
              return of(LogoutResult.Success);
            })
          );
      }

      const logoutObservable = apiLogoutObservable.pipe(
        // After (possible) API logout do local cleanup
        tap(logoutResult => {

             // Perform clientside logout
          this._isLoggedIn = false;
          this._role = UserRole.None;
          this._sessionId = '';

          this.cleanupSessionCookies();

          this.logger.info('Logout completed');

          this.onLogoutSubject.next();
        })
      );

      this.logoutInProgress = true;
      logoutObservable.subscribe(logoutResult => {
        // Let everyone know who subscribed to logoutObserved() when logout is completed
        this.logoutSubject.next(logoutResult);
        this.logoutInProgress = false;
      });
    }

    return this.logoutSubject.asObservable()
      .pipe(
        // Make sure that the subscriber unsubscribes the Observable after logout using first()
        // (because our Subject never completes)
        first(),
        // Modify observable for our needs and return it
        // (note that it is problematic that we perform multiple redirects if there are multiple subscribers...)
        tap(() => {
          if (redirectOnLogout) {
            const currentRole = this.role;
            const isCb3Login = this.isCb3;
            const isCCLogin = this.isCCLogin;
            const fromhp = (localStorage?.getItem('fromhp') === 'true');
            if (fromhp === true) localStorage?.removeItem('fromhp');
            if (currentRole === UserRole.Report || currentRole === UserRole.Parents || isCb3Login === true || isCCLogin === true) {
              this.router.navigate(['/logout']);
            } else {
              if (fromhp === true && this.isRunningAsPwa() === false) {
                this.redirectSalfeldHomepage();
              } else if (this.isParentsApp) {
                // if we delete parentsApp Cookie, restarting PWA results in "normal" login screen
                // this.isParentsApp = false;
                this.router.navigate(['/login'], {queryParams: {redirect: 'parents'}});
              } else {
                this.router.navigate([AuthService.defaultLogoutRedirectUrl]);
              }
            }
          }
        })
      );
  }

  private isRunningAsPwa(): boolean {
    return (window.matchMedia('(display-mode: standalone)').matches) || ((navigator as any).standalone) || document.referrer.includes('android-app://');
  }

  private redirectSalfeldHomepage() {
    if (this.getCurrentLanguage() === Language.English) {
      this.document.location.href = 'https://salfeld.com/en/login/';
    } else if (this.getCurrentLanguage() === Language.German) {
      this.document.location.href = 'https://salfeld.de/login/';
    } else if (this.getCurrentLanguage() === Language.French) {
      this.document.location.href = 'https://salfeld.com/fr/login/';
    }
  }


  public switchLanguage(newLang: Language) {
    this.logger.info(`Switching language to ${newLang}`);
    this.translate.use(newLang).subscribe((value) => {
      this.logger.info('Language switched');
    });

    const langAttr = this.document.createAttribute('lang');

    if (newLang === Language.German) {
      dayjs.locale(dayjsDe);
      this.bsLocaleService.use('de');

      this.cookie.deleteCookie('language');
      langAttr.value = 'de';
    } else if (newLang === Language.English) {
      // Do not import and use 'dayjs/locale/en' here because this leads to missing translations
      // This is probably caused because en is the default language of dayjs
      dayjs.locale('en');
      this.bsLocaleService.use('en');
      this.cookie.setCookie({ name: 'language', value: Language.English, path: '/', sameSite: 'Lax', maxAge: '31536000' });
      langAttr.value = 'en';
    } else if (newLang === Language.French) {
      // Do not import and use 'dayjs/locale/en' here because this leads to missing translations
      // This is probably caused because en is the default language of dayjs
      dayjs.locale('fr');
      this.bsLocaleService.use('fr');
      this.cookie.setCookie({name: 'language', value: Language.French, path: '/', sameSite: 'Lax', maxAge: '31536000'});
      langAttr.value = 'fr';
    }
    // Update lang attribute
    this.document.querySelector('html').attributes.setNamedItem(langAttr);
  }

  /** Returns current language of the portal */
  public getCurrentLanguage(): Language {
    return this.translate.currentLang as Language;
  }

  public getCurrentLanguageShort(): string {
    let retval = 'de'; // default
    if (this.translate.currentLang === Language.English) retval = 'en';
    if (this.translate.currentLang === Language.French) retval = 'fr';
    return retval;
  }

  public deleteLocalStorageReports() {
    localStorage?.removeItem('reportRangeType');
    localStorage?.removeItem('reportStartDate');
    localStorage?.removeItem('reportStopDate');
  }


  /**
   * Process response from login request and complete the login on clientside (or handle errors)
   *
   * @param roleOverride Override role which is assigned by the API by default
   */
  private processLoginResponse(data: LoginResponse, roleOverride?: UserRole): LoginResult {
    if (data.sessionID == null) {
      this.logger.error('Login request returned invalid session id');
      return LoginResult.HttpError;
    }

    // Delete old cookies to prevent unintended behaviour
    this.cleanupSessionCookies();


    if (roleOverride) {
      this._role = roleOverride;
    } else {
      switch (data.role) {
        case '':
        case null:
          this._role = UserRole.Default;
          break;
        case 'report':
          this._role = UserRole.Report;
          break;
        default:
          this.logger.error('API returned invalid role on login: ', data.role);
          break;
      }
    }

    switch (this.role) {
      case UserRole.Report:
        AuthService._defaultLoginRedirectUrl = '/readonly-report';
        break;
      case UserRole.Parents:
        AuthService._defaultLoginRedirectUrl = '/parents';
        break;
      default:
        AuthService._defaultLoginRedirectUrl = '/';
        break;
    }

    this.cookie.setCookie({ name: 'sessionId', value: data.sessionID, path: '/', sameSite: 'Lax', secure: true });
    this.cookie.setCookie({ name: 'pcId', value: data.pcId, path: '/', sameSite: 'Lax', secure: true });
    this.cookie.setCookie({ name: 'deviceType', value: data.deviceType.toString(10), path: '/', sameSite: 'Lax', secure: true });

    if (data.user != null && data.user !== '') {
      this.cookie.setCookie({ name: 'user', value: data.user, path: '/', sameSite: 'Lax', secure: true });
    }

    if (this.role !== UserRole.None && this.role !== UserRole.Default) {
      this.cookie.setCookie({ name: 'role', value: this.role.toString(), path: '/', sameSite: 'Lax', secure: true });
    }

    this._isLoggedIn = true;
    this._sessionId = data.sessionID;

    if (data.pcId) {
      this._initialActiveDevice = {
        pcId: data.pcId,
        user: data.user,
        deviceType: data.deviceType < 10 ? DeviceType.CC : DeviceType.CB
      };
    } else {
      this.logger.warn('Missing initial device on login');
    }

    this.logger.info('Login successful');
    this.onLoginSubject.next(true);
    return LoginResult.Success;
  }

  private processLoginError(error: HttpErrorResponse): LoginResult {
    this._isLoggedIn = false;

    if (error.error instanceof ErrorEvent) {
      // Client side/network error
      return LoginResult.ClientError;
    } else {
      if (error.status === 401) {
        return LoginResult.Http401;
      } else if (error.status === 403) {
        return LoginResult.HttpForbidden;   // indicates self protection / locked login
      } else {
        return LoginResult.HttpError;
      }
    }
  }

  private cleanupSessionCookies() {
    this.logger.debug('Deleting session cookies');

    this.cookie.deleteCookie('sessionId', '/');
    this.cookie.deleteCookie('pcId');
    this.cookie.deleteCookie('user');
    this.cookie.deleteCookie('deviceType');
    this.cookie.deleteCookie('role');
    this.cookie.deleteCookie('isCb3');
    this.cookie.deleteCookie('isCCLogin');
  }
}

/**
 * Role of the current user
 */
export enum UserRole {
  /**
   * Not logged in
   */
  None,
  /**
   * Default user
   */
  Default,
  /**
   * Only allowed to view reports
   */
  Report = 'report',
  /**
   * "Main view" is the parents view
   */
  Parents = 'parents'
}

/**
 * - ClientError: Error on client or network
 * - HttpError: Unsuccessful status code
 * - Http401: Wrong credentials in case of login with credentials or auth error otherwise
 */
export enum LoginResult {
  Success,
  ClientError,
  Http401,
  HttpError,
  HttpForbidden
}

/**
 * - ClientError: Error on client or network (or user is already logged out)
 * - HttpError: Unsuccessful status code
 * - Http401: Wrong credentials in case of login with credentials or auth error otherwise
 */
export enum LogoutResult {
  Success,
  ClientError,
  HttpError
}

interface LoginResponse {
  sessionID: string;
  pcId: string;
  user: string;
  role: null | '' | 'report';
  deviceType: number;
}
